import {
	Accordion,
	AccordionPanel,
	AccordionTrigger,
	Badge,
	Dialog,
	DialogBody,
	DialogHeader,
	Link,
	Text,
} from "@backstage/ui";
import {
	canArchive,
	daysIdle,
	isHoldActive,
	type BundleInventoryItem,
	type BundleReclamationRecord,
} from "@internal/backstage-plugin-avd-common";
import { makeStyles } from "@material-ui/core/styles";
import { ReactNode, useId } from "react";

import { AdminBundleHold } from "../avd/bundleHold";

import { AdminBundleArchive } from "./bundleArchive";
import {
	bundleIacVersion,
	descriptorFor,
	type BundleInventoryDescriptor,
} from "./bundleDescriptors";
import { deriveHealth, type BundleHealth, type HealthLevel } from "./bundleHealth";
import {
	absoluteTime,
	catalogPath,
	DetailFact,
	FactList,
	idleLabel,
	ownerLabel,
	relativeTime,
} from "./inventoryCells";
import { ReclamationHistory, useReclamationHistory } from "./reclamationHistory";

// The side sheet a row press opens: the one place an operator reads a bundle in full and acts on its hold.
// A BUI Dialog docked right; the hold form is a second BUI Dialog, which React Aria stacks above it.

const useStyles = makeStyles((theme) => ({
	// Unlayered, so it beats BUI's `@layer components` rules that centre the dialog.
	sheet: {
		marginLeft: "auto",
		marginRight: "var(--bui-space-3)",
		height: "calc(100vh - 2 * var(--bui-space-3))",
		maxHeight: "none",
		animation: "$slideIn 200ms ease-out",
		"@media (prefers-reduced-motion: reduce)": { animation: "none" },
	},
	"@keyframes slideIn": {
		from: { opacity: 0, transform: "translateX(8px)" },
		to: { opacity: 1, transform: "none" },
	},
	band: {
		padding: theme.spacing(1, 3),
		borderBottom: "1px solid var(--bui-border-1)",
	},
	meta: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: theme.spacing(1) },
	upn: { minWidth: 0, overflowWrap: "anywhere" },
	section: {
		padding: theme.spacing(2, 0),
		borderBottom: "1px solid var(--bui-border-1)",
		"&:last-child": { borderBottom: "none" },
	},
	heading: { margin: theme.spacing(0, 0, 1) },
	list: { margin: 0, paddingLeft: theme.spacing(2.5) },
	tiles: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: theme.spacing(1) },
	tile: {
		border: "1px solid var(--bui-border-1)",
		borderRadius: "var(--bui-radius-2)",
		padding: theme.spacing(1, 1.5),
		fontFamily: "monospace",
		wordBreak: "break-all",
	},
	timeline: {
		listStyle: "none",
		margin: theme.spacing(2, 0, 1),
		padding: 0,
		display: "grid",
		gridTemplateColumns: "repeat(3, 1fr)",
	},
	step: {
		position: "relative",
		paddingTop: theme.spacing(2),
		color: "var(--bui-fg-secondary)",
		"&::before": {
			content: '""',
			position: "absolute",
			top: 3,
			left: 0,
			right: 0,
			height: 2,
			background: "var(--bui-border-1)",
		},
		"&::after": {
			content: '""',
			position: "absolute",
			top: 0,
			left: 0,
			width: 8,
			height: 8,
			borderRadius: "50%",
			border: "2px solid var(--bui-border-1)",
			background: "var(--bui-bg-app)",
		},
	},
	done: {
		color: "var(--bui-fg-primary)",
		"&::before": { background: "var(--bui-bg-solid)" },
		"&::after": {
			background: "var(--bui-bg-solid)",
			borderColor: "var(--bui-bg-solid)",
		},
	},
	healthy: { color: "var(--bui-fg-success)" },
	attention: { color: "var(--bui-fg-warning)" },
	failed: { color: "var(--bui-fg-danger)" },
}));

const HEALTH_WORD: Record<HealthLevel, string> = {
	healthy: "Healthy",
	attention: "Attention",
	failed: "Failed",
};

const upperFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

// "Last run failed" reads inside a sentence as "last run failed"; an acronym keeps its capitals.
const lowerFirst = (text: string) =>
	/^[A-Z]{2}/.test(text) ? text : text.charAt(0).toLowerCase() + text.slice(1);

const dayMonth = (iso: string): string =>
	new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });

/** One sentence: the live state (or status), when it was last used, and the worst reason, if any. */
export function detailSummary(
	item: BundleInventoryItem,
	descriptor: Pick<BundleInventoryDescriptor, "stateText">,
	health: BundleHealth,
	now: Date,
): string {
	const state = upperFirst(descriptor.stateText?.(item) ?? item.status);
	const days = daysIdle(item.lastSeenAt, now, item.lastSeenSource);
	const used =
		days === undefined
			? "at an unknown time"
			: days === 0
				? "today"
				: `${days} ${days === 1 ? "day" : "days"} ago`;
	const worst = health.reasons.find((r) => r.level !== "info") ?? health.reasons[0];
	return `${state}, last used ${used}${worst ? `; ${lowerFirst(worst.issue)}` : ""}.`;
}

function Section(props: Readonly<{ title: string; children: ReactNode }>): ReactNode {
	const classes = useStyles();
	const id = useId();
	return (
		<section aria-labelledby={id} className={classes.section}>
			<Text as="h3" id={id} variant="title-x-small" className={classes.heading}>
				{props.title}
			</Text>
			{props.children}
		</section>
	);
}

/** The enforced reclaim whose run wrote the absent row; none for a desktop removed any other way. */
export function reclaimOutcome(
	item: Pick<BundleInventoryItem, "status">,
	rows: readonly BundleReclamationRecord[] | undefined,
	absentRunUrl: string | undefined,
): BundleReclamationRecord | undefined {
	if (item.status !== "absent" || !absentRunUrl) return undefined;
	return rows
		?.filter((row) => row.enforced && row.runUrl === absentRunUrl)
		.sort((a, b) => Date.parse(b.recordedAt) - Date.parse(a.recordedAt))[0];
}

/** Active → Warned → Reclaim, with the stages reached so far filled in; a reclaim that happened is filled too. */
export function LifecycleTimeline(
	props: Readonly<{ item: BundleInventoryItem; now: Date; reclaimed?: BundleReclamationRecord }>,
): ReactNode {
	const classes = useStyles();
	const { item, now, reclaimed } = props;
	const warned =
		reclaimed !== undefined || item.noticeStage === "warn" || item.lifecycle === "expiring";
	const held = isHoldActive(item.holdUntil, now);
	let reclaim = "Reclaim";
	if (reclaimed) reclaim = `Reclaimed on ${dayMonth(reclaimed.recordedAt)}`;
	else if (held) reclaim = "Reclaim paused by hold";
	else if (item.noticeDeadline) reclaim = `Reclaim on ${dayMonth(item.noticeDeadline)}`;
	const steps = [
		{ label: "Active", done: true },
		{ label: "Warned", done: warned },
		{ label: reclaim, done: reclaimed !== undefined },
	];
	const current = reclaimed ? 2 : warned ? 1 : 0;
	return (
		<ol className={classes.timeline} aria-label="Notice stages">
			{steps.map((step, index) => (
				<li
					key={step.label}
					className={`${classes.step} ${step.done ? classes.done : ""}`}
					aria-current={index === current ? "step" : undefined}
				>
					<Text variant="body-small">{step.label}</Text>
				</li>
			))}
		</ol>
	);
}

/** The right-docked detail of one bundle. Presentational: the table owns which row is open. */
export function BundleDetailSheet(
	props: Readonly<{
		item: BundleInventoryItem;
		types: readonly { bundleType: string; displayName?: string }[];
		descriptors: readonly BundleInventoryDescriptor[];
		now: Date;
		onClose: () => void;
		onChanged?: () => void;
	}>,
): ReactNode {
	const classes = useStyles();
	const { item, descriptors, now } = props;
	const descriptor = descriptorFor(descriptors, item.bundleType);
	const removalRun = descriptor.runUrl?.(item);
	// A refresh that turns this bundle absent, or moves its removal run, must re-read the log.
	const history = useReclamationHistory(item, `${item.status}|${removalRun ?? ""}`);
	const reclaimed = reclaimOutcome(item, history.rows, removalRun);
	const removedUnreclaimed = item.status === "absent" && !reclaimed;
	const Details = descriptor.Details;
	const health = deriveHealth(item, descriptor, now);
	const version = bundleIacVersion(item, descriptor);
	const typeName = props.types.find((t) => t.bundleType === item.bundleType)?.displayName;
	const typeLabel = typeName ?? item.bundleType;
	const checked = item.driftCheckedAt;
	const entityPath = item.owner.entityRef ? catalogPath(item.owner.entityRef) : undefined;

	return (
		<Dialog
			width={520}
			className={classes.sheet}
			isOpen
			onOpenChange={(open) => !open && props.onClose()}
		>
			<DialogHeader>{item.owner.displayName ?? ownerLabel(item)}</DialogHeader>
			<div className={classes.band}>
				<div className={classes.meta}>
					<Badge size="small">{typeLabel}</Badge>
					<Badge size="small" className={classes[health.level]}>
						{HEALTH_WORD[health.level]}
					</Badge>
					{item.owner.displayName ? (
						<Text variant="body-small" color="secondary" className={classes.upn}>
							{ownerLabel(item)}
						</Text>
					) : null}
				</div>
				<Text as="p" variant="body-medium">
					{detailSummary(item, descriptor, health, now)}
				</Text>
			</div>
			<DialogBody>
				{health.reasons.length ? (
					<Section title="Health reasons">
						<ul className={classes.list}>
							{health.reasons.map((r) => (
								<li key={r.id}>
									<Text as="span" weight="bold">
										{r.issue}.
									</Text>{" "}
									<Text as="span">{r.why}</Text>
								</li>
							))}
						</ul>
					</Section>
				) : null}
				{Details ? (
					<Section title={upperFirst(descriptor.noun)}>
						<Details item={item} onChanged={props.onChanged} />
					</Section>
				) : null}
				<Section title="IaC & drift">
					<div className={classes.tiles}>
						<div className={classes.tile}>
							<Text as="div" variant="body-small" color="secondary">
								Desired
							</Text>
							{version.desired ?? "—"}
						</div>
						<div className={classes.tile}>
							<Text as="div" variant="body-small" color="secondary">
								Actual
							</Text>
							{version.actual ?? "—"}
						</div>
					</div>
					<FactList>
						<DetailFact label="Version" value={version.state} />
						<DetailFact
							label="Drift check"
							value={
								item.driftStatus
									? `${item.driftStatus}${checked ? `, ${relativeTime(checked, now)}` : ""}`
									: "never checked"
							}
						/>
						{checked ? (
							<DetailFact label="Checked at" value={absoluteTime(checked)} />
						) : null}
					</FactList>
				</Section>
				{removedUnreclaimed && history.rows ? null : (
					<Section title="Lifecycle">
						{removedUnreclaimed ? (
							<Text as="p" color="secondary">
								{history.error
									? "Whether this was reclaimed cannot be read: the reclamation log is unavailable."
									: "Reading the reclamation log…"}
							</Text>
						) : (
							<>
								<FactList>
									{reclaimed ? (
										<>
											<DetailFact label="Lifecycle" value="reclaimed" />
											<DetailFact
												label="Reclaimed"
												value={absoluteTime(reclaimed.recordedAt)}
											/>
											<DetailFact
												label="Idle at reclaim"
												value={`${reclaimed.daysIdle} d`}
											/>
										</>
									) : (
										<>
											<DetailFact label="Idle" value={idleLabel(item, now)} />
											<DetailFact label="Lifecycle" value={item.lifecycle} />
											<DetailFact
												label="Reclaim date"
												value={
													item.noticeDeadline
														? absoluteTime(item.noticeDeadline)
														: undefined
												}
											/>
										</>
									)}
								</FactList>
								<LifecycleTimeline item={item} now={now} reclaimed={reclaimed} />
							</>
						)}
					</Section>
				)}
				{descriptor.hasRegistryRow?.(item) === false ? null : (
					<Section title="Hold">
						<AdminBundleHold
							bundleType={item.bundleType}
							bundleId={item.bundleId}
							slug={item.slug}
							holdUntil={item.holdUntil}
							holdReason={item.holdReason}
							holdSetBy={item.holdSetBy}
							onChanged={props.onChanged}
						/>
					</Section>
				)}
				{descriptor.hasRegistryRow?.(item) === false ? null : (
					<ArchiveSection item={item} onChanged={props.onChanged} />
				)}
				<Section title="Reclamation history">
					<ReclamationHistory history={history} />
				</Section>
				<Accordion>
					<AccordionTrigger title="Identity" />
					<AccordionPanel>
						<FactList>
							<DetailFact label="Last use" value={lastUse(item)} />
							<DetailFact label="Bundle type" value={item.bundleType} mono />
							<DetailFact label="Bundle id" value={item.bundleId} mono />
							<DetailFact label="Slug / tfstate key" value={item.slug} mono />
							<DetailFact label="Catalog entity" value={item.owner.entityRef} mono />
							<DetailFact
								label="Last registry write"
								value={item.updatedAt ? absoluteTime(item.updatedAt) : undefined}
							/>
						</FactList>
						{entityPath ? <Link href={entityPath}>Open in the catalog</Link> : null}
					</AccordionPanel>
				</Accordion>
			</DialogBody>
		</Dialog>
	);
}

/** Only a row that can be archived or already is gets the section; a live desktop's sheet stays as it was. */
function ArchiveSection(
	props: Readonly<{ item: BundleInventoryItem; onChanged?: () => void }>,
): ReactNode {
	if (!props.item.archivedAt && !canArchive(props.item.status)) return null;
	return (
		<Section title="Registry row">
			<AdminBundleArchive item={props.item} onChanged={props.onChanged} />
		</Section>
	);
}

const lastUse = (item: BundleInventoryItem): string =>
	item.lastSeenAt && item.lastSeenSource !== "unknown"
		? absoluteTime(item.lastSeenAt)
		: "unknown";
